import { useEffect, useState } from 'react';
import {
  CHORD_QUALITIES,
  MAX_CONTROL_VALUE,
  MAX_MIDI_NOTE,
  MAX_VELOCITY,
  MIN_CONTROL_VALUE,
  MIN_MIDI_NOTE,
  MIN_VELOCITY,
  type ChordQuality,
  type SequenceEvent,
} from '../../format/types';

interface EventInspectorProps {
  readonly event: SequenceEvent | undefined;
  readonly onPatch: (eventId: string, patch: Record<string, unknown>) => void;
  readonly onDelete: (eventId: string) => void;
}

const MIN_CHORD_DEGREE = 1;
const MAX_CHORD_DEGREE = 7;

export function EventInspector(props: EventInspectorProps): JSX.Element {
  const { event } = props;
  if (!event) {
    return (
      <div className="event-inspector empty">
        <p>Kein Step ausgewaehlt. Klicke einen belegten Step, um ihn zu bearbeiten.</p>
      </div>
    );
  }

  return (
    <div className="event-inspector">
      <h3>Step {event.step + 1} &middot; {event.kind}</h3>
      {event.kind === 'note' && (
        <>
          <label className="field">
            Note (MIDI 0-127)
            <NumericEventInput
              value={event.note}
              min={MIN_MIDI_NOTE}
              max={MAX_MIDI_NOTE}
              step={1}
              integer
              placeholder={event.scaleDegree !== undefined ? 'nicht gesetzt' : ''}
              onCommit={(value) => props.onPatch(event.id, { note: value, scaleDegree: undefined })}
            />
          </label>
          <label className="field">
            Skalenstufe (alternativ)
            <NumericEventInput
              value={event.scaleDegree}
              step={1}
              integer
              placeholder={event.note !== undefined ? 'nicht gesetzt' : ''}
              onCommit={(value) => props.onPatch(event.id, { scaleDegree: value, note: undefined })}
            />
          </label>
          <VelocityGateFields event={event} onPatch={props.onPatch} />
        </>
      )}
      {event.kind === 'chord' && (
        <>
          <label className="field">
            Grundton (Skalenstufe)
            <NumericEventInput
              value={event.degree}
              min={MIN_CHORD_DEGREE}
              max={MAX_CHORD_DEGREE}
              step={1}
              integer
              onCommit={(value) => props.onPatch(event.id, { degree: value })}
            />
          </label>
          <label className="field">
            Qualitaet
            <select value={event.quality} onChange={(e) => props.onPatch(event.id, { quality: e.target.value as ChordQuality })}>
              {CHORD_QUALITIES.map((quality) => (
                <option key={quality} value={quality}>
                  {quality}
                </option>
              ))}
            </select>
          </label>
          <VelocityGateFields event={event} onPatch={props.onPatch} />
        </>
      )}
      {event.kind === 'drum' && (
        <>
          <label className="field">
            Trigger-ID
            <input value={event.triggerId} onChange={(e) => props.onPatch(event.id, { triggerId: e.target.value })} />
          </label>
          <label className="field">
            Velocity
            <NumericEventInput
              value={event.velocity}
              min={MIN_VELOCITY}
              max={MAX_VELOCITY}
              step={1}
              integer
              onCommit={(value) => props.onPatch(event.id, { velocity: value })}
            />
          </label>
        </>
      )}
      {event.kind === 'control' && (
        <>
          <label className="field">
            Control-ID
            <input value={event.controlId} onChange={(e) => props.onPatch(event.id, { controlId: e.target.value })} />
          </label>
          <label className="field">
            Wert (0..1)
            <NumericEventInput
              value={event.value}
              min={MIN_CONTROL_VALUE}
              max={MAX_CONTROL_VALUE}
              step="any"
              onCommit={(value) => props.onPatch(event.id, { value })}
            />
          </label>
          <label className="field">
            State-Referenz (optional)
            <input value={event.stateRef ?? ''} onChange={(e) => props.onPatch(event.id, { stateRef: e.target.value || undefined })} />
          </label>
          <label className="field">
            Szenen-Referenz (optional)
            <input value={event.sceneRef ?? ''} onChange={(e) => props.onPatch(event.id, { sceneRef: e.target.value || undefined })} />
          </label>
        </>
      )}
      <button type="button" className="danger" onClick={() => props.onDelete(event.id)}>
        Step loeschen
      </button>
    </div>
  );
}

function VelocityGateFields(props: {
  readonly event: { readonly id: string; readonly velocity: number; readonly gate: number };
  readonly onPatch: (eventId: string, patch: Record<string, unknown>) => void;
}): JSX.Element {
  return (
    <>
      <label className="field">
        Velocity (0-127)
        <NumericEventInput
          value={props.event.velocity}
          min={MIN_VELOCITY}
          max={MAX_VELOCITY}
          step={1}
          integer
          onCommit={(value) => props.onPatch(props.event.id, { velocity: value })}
        />
      </label>
      <label className="field">
        Gate (Steps)
        <NumericEventInput
          value={props.event.gate}
          min={0}
          step="any"
          minExclusive
          onCommit={(value) => props.onPatch(props.event.id, { gate: value })}
        />
      </label>
    </>
  );
}

interface NumericEventInputProps {
  readonly value: number | undefined;
  readonly min?: number;
  readonly max?: number;
  readonly step: number | 'any';
  readonly integer?: boolean;
  readonly minExclusive?: boolean;
  readonly placeholder?: string;
  readonly onCommit: (value: number) => void;
}

function NumericEventInput(props: NumericEventInputProps): JSX.Element {
  const [draft, setDraft] = useState(props.value === undefined ? '' : String(props.value));

  useEffect(() => {
    setDraft(props.value === undefined ? '' : String(props.value));
  }, [props.value]);

  function commit(): void {
    const parsed = draft.trim() === '' ? Number.NaN : Number(draft);
    const belowMinimum = props.min !== undefined && (props.minExclusive ? parsed <= props.min : parsed < props.min);
    const aboveMaximum = props.max !== undefined && parsed > props.max;
    if (!Number.isFinite(parsed) || belowMinimum || aboveMaximum || (props.integer === true && !Number.isInteger(parsed))) {
      setDraft(props.value === undefined ? '' : String(props.value));
      return;
    }
    setDraft(String(parsed));
    props.onCommit(parsed);
  }

  return (
    <input
      type="number"
      {...(props.min !== undefined ? { min: props.min } : {})}
      {...(props.max !== undefined ? { max: props.max } : {})}
      step={props.step}
      value={draft}
      placeholder={props.placeholder}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') commit();
      }}
    />
  );
}
